import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from './Button.jsx';
import { useAssociates } from '../hooks/useAssociates.js';
import { useCreateAccount, useUpdateAccount } from '../hooks/useAssociateAccounts.js';

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

function Field({ label, children, error, required }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm() {
  return { associate_id: '', ownership_pct: '', opened_at: todayIso(), notes: '', status: 'active' };
}

function formFromAccount(account) {
  if (!account) return emptyForm();
  return {
    associate_id:  account.associate_id ?? '',
    ownership_pct: account.ownership_pct != null ? String(account.ownership_pct) : '',
    opened_at:     account.opened_at ?? todayIso(),
    notes:         account.notes ?? '',
    status:        account.status ?? 'active',
  };
}

function validate(form, isEdit) {
  const errors = {};
  if (!isEdit && !form.associate_id) errors.associate_id = 'Associé requis';
  if (form.ownership_pct !== '') {
    const pct = parseFloat(form.ownership_pct);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      errors.ownership_pct = 'Pourcentage entre 0 et 100';
    }
  }
  if (!form.opened_at) errors.opened_at = 'Date d\'ouverture requise';
  return errors;
}

/**
 * AssociateAccountForm — slide-over modal pour ouvrir ou éditer un compte
 * courant d'associé dans la société courante.
 *
 * Props :
 *   open                : boolean
 *   onClose             : () => void
 *   account             : object | undefined — si fourni, mode édition (ownership_pct/notes/status seulement)
 *   defaultAssociateId  : string | undefined — pré-sélectionne l'associé en mode création
 */
export default function AssociateAccountForm({ open, onClose, account, defaultAssociateId }) {
  const isEdit = Boolean(account);
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const mutation = isEdit ? updateAccount : createAccount;

  const { data: associatesData } = useAssociates('active');
  const associates = Array.isArray(associatesData) ? associatesData : [];

  const [form, setForm]     = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      const base = formFromAccount(account);
      if (!isEdit && defaultAssociateId) {
        base.associate_id = defaultAssociateId;
      }
      setForm(base);
      setErrors({});
      mutation.reset?.();
    }
  }, [open, account?.id, defaultAssociateId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose?.(); }
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form, isEdit);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const pct = form.ownership_pct !== '' ? parseFloat(form.ownership_pct) : null;

    if (isEdit) {
      const payload = {
        id:            account.id,
        ownership_pct: pct,
        notes:         form.notes.trim() || null,
        status:        form.status,
      };
      await mutation.mutateAsync(payload);
    } else {
      const payload = {
        associate_id:  form.associate_id,
        ownership_pct: pct,
        opened_at:     form.opened_at || null,
        notes:         form.notes.trim() || null,
      };
      await mutation.mutateAsync(payload);
    }

    onClose?.();
  }

  const isDisabled = mutation.isPending;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 bg-black/30
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Modifier le compte associé' : 'Ouvrir un compte associé'}
        className={`
          fixed z-50 bg-white shadow-2xl
          transition-transform duration-200 ease-out
          inset-x-0 bottom-0 rounded-t-2xl max-h-[95dvh] overflow-y-auto
          ${open ? 'translate-y-0' : 'translate-y-full'}
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[480px] lg:rounded-none lg:max-h-none
          ${open ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Modifier le compte' : 'Ouvrir un compte associé'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-5 space-y-4">
          {!isEdit && (
            <Field label="Associé" error={errors.associate_id} required>
              <select
                className={inputClass}
                value={form.associate_id}
                onChange={(e) => set('associate_id', e.target.value)}
                disabled={isDisabled}
              >
                <option value="">-- Sélectionner un associé --</option>
                {associates.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.display_name ?? `${a.first_name} ${a.last_name}`}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {isEdit && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm text-slate-600">
              L'associé ne peut pas être modifié sur un compte existant.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Part de détention (%)" error={errors.ownership_pct}>
              <input
                type="number"
                className={inputClass}
                value={form.ownership_pct}
                onChange={(e) => set('ownership_pct', e.target.value)}
                min="0"
                max="100"
                step="0.01"
                placeholder="50.00"
                disabled={isDisabled}
              />
            </Field>

            {!isEdit && (
              <Field label="Date d'ouverture" error={errors.opened_at} required>
                <input
                  type="date"
                  className={inputClass}
                  value={form.opened_at}
                  onChange={(e) => set('opened_at', e.target.value)}
                  disabled={isDisabled}
                />
              </Field>
            )}

            {isEdit && (
              <Field label="Statut">
                <select
                  className={inputClass}
                  value={form.status}
                  onChange={(e) => set('status', e.target.value)}
                  disabled={isDisabled}
                >
                  <option value="active">Actif</option>
                  <option value="closed">Clôturé</option>
                </select>
              </Field>
            )}
          </div>

          <Field label="Notes">
            <textarea
              className={`${inputClass} resize-none`}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              placeholder="Informations complémentaires…"
              disabled={isDisabled}
            />
          </Field>

          {mutation.error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {mutation.error.message}
            </p>
          )}

          <div className="flex gap-2 pt-2 pb-safe">
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={isDisabled}
              className="flex-1"
            >
              {isEdit ? 'Enregistrer' : 'Ouvrir le compte'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isDisabled}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
